import * as actionTypes from '../actions/actionTypes';
import { CTADataType, between, calculateTimeDifference, getCtaTrainColor, isRouteCTATrain } from '../../shared/utility';
import { 
    KEY_ROUTE, 
    KEY_STOP,
    KEY_DESTINATION,
    KEY_TRAIN_LINE,
    CTA_L_TRAINS
} from '../../shared/constants';

const initialState = {
    ctaBusRoutes: [],
    ctaTrainRoutes: CTA_L_TRAINS,
    destinations: [],
    trainStations: [],
    busStops: [],
    predictions: [],
    loading: false,
    selectedRoute: JSON.parse(localStorage.getItem(KEY_ROUTE)) || null,
    selectedDestination: localStorage.getItem(KEY_DESTINATION) || null,
    selectedStop: JSON.parse(localStorage.getItem(KEY_STOP)) || null,
    selectedTrainLine: localStorage.getItem(KEY_TRAIN_LINE) || null,
    message: null 
};

const getStart = (state, action) => {
    return {
        ...state,
        loading: action.loading
    };
};

const getFailed = (state, action) => {
    return {
        ...state,
        loading: action.loading
    };
};

const setCtaBusRoutes = (state, action) => {
    localStorage.clear();
    const busRoutes = action.ctaBusRoutes.map(route => ({
        type: CTADataType.ROUTE,
        routeId: route.rt,
        routeName: route.rtnm,
        routeColor: route.rtclr
    }));
    return {
        ...state,
        ctaBusRoutes: busRoutes,
        loading: action.loading,
        selectedStopId: null,
        predictions: [] 
    };
};

const setCtaTrainRoutes = (state, action) => {
    localStorage.clear();
    return {
        ...state,
        ctaTrainRoutes: action.ctaTrainRoutes,
        loading: action.loading,
        selectedStopId: null,
        predictions: []
    };
};

const setSelectedRoute = (state, action) => {
    localStorage.setItem(KEY_ROUTE, JSON.stringify(action.route));
    return {
        ...state,
        selectedRoute: {...action.route} // routeId, routeColor
    };
};

const setSelectedDestination = (state, action) => {
    localStorage.setItem(KEY_DESTINATION, action.destination);
    return {
        ...state,
        selectedDestination: action.destination
    };
};

const setSelectedBusStop = (state, action) => {
    localStorage.setItem(KEY_STOP, JSON.stringify(action.busStop));
    return {
        ...state,
        selectedStop: {...action.busStop}
    };
};

const setSelectedTrainStation = (state, action) => {
    localStorage.setItem(KEY_STOP, JSON.stringify(action.trainStation));
    return {
        ...state,
        selectedStop: {...action.trainStation}
    };
};

const setSelectedTrainLine = (state, action) => {
    localStorage.setItem(KEY_TRAIN_LINE, action.trainLine);
    return {
        ...state,
        selectedTrainLine: action.trainLine
    };
};

const setBusRouteDestinations = (state, action) => {
    const destinations = action.destinations.map(dest => ({
        type: CTADataType.DESTINATION,
        destinationName: dest.dir
    }));
    return {
        ...state,
        destinations: destinations,
        loading: action.loading
    };
};

const setBusStops = (state, action) => {
    const busStops = action.busStops.map(stop => ({
        type: CTADataType.BUS_STOP,
        stopId: stop.stpid,
        stopName: stop.stpnm
    }));
    return {
        ...state,
        busStops: busStops,
        loading: action.loading
    };
};

const getRouteColor = (routes, routeId) => {
    if (routes && routes.length > 0) {
        if (!isRouteCTATrain(routeId)) {
            const route = routes.find(r => r.routeId === routeId);
            if (route) return route.routeColor;
        } 
        // if train or if above is not found in cta train cause route Id is different for both cta and socrata;
        // getCtaTrainColor handles it.
        return getCtaTrainColor(routeId);
    }
    return '#000000'; // black
}

const setBusStopPredictions = (state, action) => {
    const predictions = action.predictions.map(pred => ({
        type: CTADataType.PREDICTION,
        predictionTime: pred.prdctdn,
        destination: pred.des,
        route: pred.rt,
        routeColor: getRouteColor(state.ctaBusRoutes, pred.rt),
        vehicleId: pred.vid
    }));
    return {
        ...state,
        predictions: predictions,
        loading: action.loading,
        message: null
    };
};

const setTrainStationPredictions = (state, action) => {
    const predictions = action.predictions.map(pred => ({
        type: CTADataType.ARRIVAL,
        predictionTime: calculateTimeDifference(pred.arrT, pred.prdt),
        trainRunNumber: pred.rn,
        stopName: pred.staNm,
        stopId: pred.stpId,
        destination: pred.destNm,
        delay: pred.isDly === '1',
        scheduled: pred.isSch === '1',
        route: pred.rt,
        routeColor: getRouteColor(state.ctaTrainRoutes, pred.rt) 
    }));
    console.log(predictions);
    return {
        ...state,
        predictions: predictions,
        loading: action.loading,
        message: null
    };
};

const setTrainStations = (state, action) => {
    const { selectedTrainLine } = state;
    const routeColor = getCtaTrainColor(selectedTrainLine);
    const trainStations = action.trainStations.map(station => ({
        type: CTADataType.TRAIN_STATION,
        stopName: station.station_name,
        stopId: station.stop_id,
        destination: between(station.stop_name, '(', ')'),
        routeColor: routeColor
    }));
    return {
        ...state,
        trainStations: trainStations,
        loading: action.loading
    };
};



const noServiceScheduled = (state, action) => {
    return {
        ...state,
        message: action.message,
        predictions: action.predictions,
        loading: action.loading
    };
};

const reducer = (state = initialState, action) => {
    console.log(action.type);
    switch (action.type) {
        case actionTypes.GET_START: return getStart(state, action);
        case actionTypes.GET_FAILED: return getFailed(state, action);
        case actionTypes.SET_CTA_BUS_ROUTES: return setCtaBusRoutes(state, action);
        case actionTypes.SET_SELECTED_ROUTE: return setSelectedRoute(state, action);
        case actionTypes.SET_BUS_ROUTE_DESTINATIONS: return setBusRouteDestinations(state, action);
        case actionTypes.SET_SELECTED_DESTINATION: return setSelectedDestination(state, action);
        case actionTypes.SET_BUS_STOPS: return setBusStops(state, action);
        case actionTypes.SET_SELECTED_BUS_STOP: return setSelectedBusStop(state, action);
        case actionTypes.SET_BUS_STOP_PREDICTIONS: return setBusStopPredictions(state, action);
        case actionTypes.NO_SERVICE_SCHEDULED: return noServiceScheduled(state, action);
        case actionTypes.SET_CTA_TRAIN_ROUTES: return setCtaTrainRoutes(state, action);
        case actionTypes.SET_SELECTED_TRAIN_LINE: return setSelectedTrainLine(state, action);
        case actionTypes.SET_TRAIN_STATIONS: return setTrainStations(state, action);
        case actionTypes.SET_SELECTED_TRAIN_STATION: return setSelectedTrainStation(state, action);
        case actionTypes.SET_TRAIN_STATION_PREDICTIONS: return setTrainStationPredictions(state, action);
        default: return state;
    }
};

export default reducer;


